// ============================================
// HEROSHHI - SIGN UP
// File: src/pages/auth/SignupPage.jsx
// Same request and checks as before (8+ characters with a letter and a
// number, 10+ digit phone), and the Meta CompleteRegistration event fires
// only after the account is really created. "Confirm password" is gone:
// the show/hide control lets people check what they typed.
// ============================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import config from '../../config';
import AuthLayout, { Field, Notice, PasswordInput, PasswordChecklist, SubmitButton, passwordOk } from './AuthLayout';
import EmailCodeStep from './EmailCodeStep';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    businessName: '',
    businessType: 'Generic Business'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [codeEmail, setCodeEmail] = useState('');   // ✉️ waiting for the emailed code

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!passwordOk(formData.password)) {
      setError('Your password needs at least 8 characters, with a letter and a number.');
      return;
    }
    const digits = formData.phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setError(`Your phone number needs at least 10 digits. You've entered ${digits.length}.`);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${config.API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          businessName: formData.businessName,
          businessType: formData.businessType
        })
      });
      const data = await response.json();
      if (data.success) {
        if (data.needsVerification) {
          setCodeEmail(data.email || formData.email);   // Meta's event fires once the code is confirmed
        } else {
          if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: formData.businessType });
          setSuccess(true);
        }
      } else {
        setError(data.message || 'We couldn\'t create your account. Please try again.');
      }
    } catch (err) {
      setError('We couldn\'t reach Heroshhi. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---------- the emailed code ----------
  if (codeEmail) {
    return (
      <EmailCodeStep
        email={codeEmail}
        backLabel="Wrong email? Start again"
        onBack={() => setCodeEmail('')}
        onVerified={(user, token) => {
          if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: formData.businessType });
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
          window.location.assign('/');
        }}
      />
    );
  }

  // ---------- after signing up ----------
  if (success) {
    return (
      <AuthLayout>
        <h1>Check your email to finish</h1>
        <p className="hs-auth-sub">We've sent a link to confirm your email address:</p>
        <span className="hs-email-chip">{formData.email}</span>
        <p className="hs-auth-sub">Open it, and you're in. If it hasn't arrived within a minute or two, look in your spam folder.</p>
        <Link to="/login" className="hs-btn" style={{ textDecoration: 'none' }}>Continue to sign in</Link>
      </AuthLayout>
    );
  }

  const pw = formData.password;
  return (
    <AuthLayout wide>
      <h1>Create your free account</h1>
      <p className="hs-auth-sub">Free forever, no card needed. It takes about a minute.</p>

      {error && <Notice>{error}</Notice>}

      <form onSubmit={handleSubmit}>
        <div className="hs-two">
          <Field id="name" label="Your name">
            <input id="name" type="text" name="name" value={formData.name} onChange={handleChange}
              className="hs-input" placeholder="Full name" required autoComplete="name" autoFocus />
          </Field>
          <Field id="phone" label="Phone number">
            <input id="phone" type="tel" name="phone" value={formData.phone} onChange={handleChange}
              className="hs-input" placeholder="+91 98765 43210" required autoComplete="tel" inputMode="tel" />
          </Field>
        </div>

        <div className="hs-two">
          <Field id="businessName" label="Business name">
            <input id="businessName" type="text" name="businessName" value={formData.businessName} onChange={handleChange}
              className="hs-input" placeholder="Clinic, school or company" required autoComplete="organization" />
          </Field>
          <Field id="businessType" label="Kind of business" hint="Sets the AI assistant's safety rules. You can change it later.">
            <select id="businessType" name="businessType" value={formData.businessType} onChange={handleChange} className="hs-input" required>
              <option value="Generic Business">Other business</option>
              <option value="Clinic">Clinic or hospital</option>
              <option value="School">School or institute</option>
            </select>
          </Field>
        </div>

        <Field id="email" label="Email">
          <input id="email" type="email" name="email" value={formData.email} onChange={handleChange}
            className="hs-input" placeholder="you@yourbusiness.com" required autoComplete="email" />
        </Field>

        <Field id="password" label="Password">
          <PasswordInput id="password" name="password" value={pw} onChange={handleChange}
            placeholder="Choose a password" autoComplete="new-password" />
          <PasswordChecklist password={pw} />
        </Field>

        <SubmitButton loading={loading} loadingText="Creating your account">Create my account</SubmitButton>
        <p className="hs-auth-legal">
          By creating an account, you agree to our{' '}
          <a href="https://heroshhi.com/legal.html#terms" target="_blank" rel="noopener noreferrer">terms</a> and{' '}
          <a href="https://heroshhi.com/legal.html#privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
        </p>
      </form>

      <p className="hs-auth-foot">Already have an account? <Link to="/login">Sign in</Link></p>
    </AuthLayout>
  );
};

export default SignupPage;